import React, { Component } from 'react';
import PropTypes from 'prop-types';
import ContentLoader from 'react-content-loader';
// @material-ui/icons
import Search from '@material-ui/icons/Search';
import Swap from '@material-ui/icons/SwapHoriz';
import ArrowLeft from '@material-ui/icons/ArrowBack';
import ArrowRight from '@material-ui/icons/ArrowForward';
// @material-ui/core components
import { List, ListItem } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
import InputAdornment from '@material-ui/core/InputAdornment/InputAdornment';
// core components
import GridItem from 'components/UI/Grid/GridItem';
import Button from 'components/UI/CustomButtons/Button';
import GridContainer from 'components/UI/Grid/GridContainer';
import CustomInput from 'components/UI/CustomInput/CustomInput';
// our components
import PermissionGroupStyles from 'assets/jss/PermissionSelect';
import { withTranslation } from 'react-i18next';

/**
 * @class
 * @extends Component
 */
class PermissionGroupSelect extends Component {
  constructor(props) {
    super(props);
    this.timeoutId = 0;
    this.state = {
      newSelectedItems: [],
      focused: '',
    };
  }

  /**
   * @description
   * Callback for handle when a permission is selected.
   *
   * @param {Object} elem - Permission clicked.
   * @param {string} sel - Selected side.
   * @returns {undefined} No return.
   */
  handleItemSelected = (elem, sel) => {
    const { newSelectedItems, focused } = this.state;

    let newPermissions = [];
    let side = sel;
    // if side is not focused reset the selected list only with the new.
    // else if the clicked item is in the focused side then:
    // else if the clicked item is already in the selected list then remove it,
    // else added to list.
    if (side !== focused) {
      newPermissions.push(elem);
    } else if (newSelectedItems.includes(elem)) {
      newPermissions = newSelectedItems.filter(perm => perm !== elem);
      // after remove the item, if  the list is empty then remove the focus.
      if (!newPermissions.length) {
        side = '';
      }
    } else {
      newPermissions = [...newSelectedItems, elem];
    }
    this.setState({ newSelectedItems: newPermissions, focused: side });
  };

  /**
   * @description
   * Callback for handle when the add button is clicked.
   *
   * @param {Object} e - Event object.
   * @returns {undefined} No return.
   */
  handleAddClick = e => {
    const { updateItem, selectedItems } = this.props;
    e.preventDefault();
    const { newSelectedItems, focused } = this.state;
    let resultPermissions = [];
    // If the focused side is left(items) then add item
    // to the selected list.
    // If the focused side is right(items) then remove item
    // from selected list
    // If no focused then return.
    switch (focused) {
      case 'left':
        resultPermissions = [...selectedItems, ...newSelectedItems];
        break;
      case 'right':
        resultPermissions = selectedItems.filter(
          perm => !newSelectedItems.includes(perm),
        );
        break;

      default:
        return;
    }
    this.setState(
      {
        newSelectedItems: [],
        focused: '',
      },
      () => {
        updateItem(resultPermissions);
      },
    );
  };

  /**
   * @description
   * Callback for handle the search input.
   *
   * @param {Object} e - Event object.
   * */
  handleSearch = e => {
    e.persist();
    clearTimeout(this.timeoutId);
    this.timeoutId = setTimeout(() => {
      const { searchItem } = this.props;
      const { newSelectedItems } = this.state;
      const query = e.target.value;
      if (newSelectedItems.length > 0) {
        this.setState(
          {
            newSelectedItems: [],
            focused: '',
          },
          () => {
            searchItem(query);
          },
        );
      } else {
        searchItem(query);
      }
    }, 500);
  };

  /**
   * @description
   * Render the added groups.
   *
   * @returns {*} More items.
   */
  renderExtraData() {
    const { classes, extraData, itemRender } = this.props;

    if (extraData && extraData.byGroup) {
      return extraData.groups.map(({ id, name, items }) => (
        <div key={id} className={classes.addedGroupsContainer}>
          <div className={classes.groupHeader}>
            <h6>{name}</h6>
          </div>
          <List className={classes.permList}>
            {items.map(data => (
              <ListItem
                key={data.id}
                className={`${classes.permItem} 
                ${classes.permItemBtn} disabled`}
              >
                {itemRender(data)}
              </ListItem>
            ))}
          </List>
        </div>
      ));
    }
    return '';
  }

  /**
   * @description
   * Render the empty space.
   *
   * @returns {*} No permissions.
   */
  renderNoPermissions() {
    const {
      classes,
      selectedItems,
      extraData,
      t,
      translatePrefix,
    } = this.props;
    if (extraData && selectedItems.length === 0) {
      return (
        <p className={classes.emptySpace}>
          {t(`${translatePrefix}.select.empty`)}
        </p>
      );
    }
    return null;
  }

  /**
   * @description
   * Render Permission Select component.
   */
  render() {
    const {
      classes,
      loading,
      items,
      selectedItems,
      t,
      translatePrefix,
      itemRender,
    } = this.props;
    const { newSelectedItems, focused } = this.state;

    const itemsFiltered =
      loading && !items
        ? []
        : items.edges.filter(
            inputItem =>
              !selectedItems.filter(
                addedItem => addedItem.id === inputItem.node.id,
              ).length,
          );

    const showHeader = !!(selectedItems && selectedItems.length > 0);

    return (
      <GridContainer className={classes.gridContainer} alignItems="center">
        <GridItem xs={12} sm={5}>
          <div className={classes.groupContainer}>
            <CustomInput
              id="searchPermissions"
              inputProps={{
                placeholder: t(`${translatePrefix}.select.placeholder`),
                inputProps: {
                  'aria-label': 'Search',
                },
                startAdornment: (
                  <InputAdornment position="end">
                    <Search className={classes.inputAdornmentIcon} />
                  </InputAdornment>
                ),
                onChange: this.handleSearch,
              }}
              className={classes.groupSearch}
            />

            <div className={`${classes.permListContainer} leftPermList`}>
              {loading ? (
                <ContentLoader
                  height={374}
                  width={279}
                  speed={2}
                  primaryColor="#f3f3f3"
                  secondaryColor="#e1e0e2"
                >
                  <path
                    // eslint-disable-next-line max-len
                    d="M16,16 L260,16 C262.209139,16 264,17.790861 264,20 L264,50 L12,50 L12,20 C12,17.790861 13.790861,16 16,16 Z M16,94.25 L260,94.25 C262.209139,94.25 264,96.040861 264,98.25 L264,124.25 C264,126.459139 262.209139,128.25 260,128.25 L16,128.25 C13.790861,128.25 12,126.459139 12,124.25 L12,98.25 C12,96.040861 13.790861,94.25 16,94.25 Z M12,55.125 L264,55.125 L264,89.125 L12,89.125 L12,55.125 Z M12,133.375 L264,133.375 L264,167.375 L12,167.375 L12,133.375 Z M12,172.5 L264,172.5 L264,206.5 L12,206.5 L12,172.5 Z M12,250.75 L264,250.75 L264,284.75 L12,284.75 L12,250.75 Z M12,211.625 L264,211.625 L264,245.625 L12,245.625 L12,211.625 Z M12,289.875 L264,289.875 L264,323.875 L12,323.875 L12,289.875 Z M12,329 L264,329 L264,359 C264,361.209139 262.209139,363 260,363 L16,363 C13.790861,363 12,361.209139 12,359 L12,329 Z M0,0 L1,0 L1,1 L0,1 L0,0 Z M278,0 L279,0 L279,1 L278,1 L278,0 Z M0,373 L1,373 L1,374 L0,374 L0,373 Z M278,373 L279,373 L279,374 L278,374 L278,373 Z"
                    fill="#F3F3F3"
                  />
                </ContentLoader>
              ) : (
                <List className={classes.permList}>
                  {itemsFiltered.map(data => (
                    <ListItem
                      id={`permission-select-list-${data.node.id}`}
                      key={`permission-${data.node.id}`}
                      className={`${
                        classes.permItem
                      } ${newSelectedItems.includes(data.node) && 'selected'}`}
                    >
                      <Button
                        onClick={() => {
                          this.handleItemSelected(data.node, 'left');
                        }}
                        simple
                        color="primary"
                        className={classes.permItemBtn}
                      >
                        {itemRender(data.node)}
                      </Button>
                    </ListItem>
                  ))}
                </List>
              )}
            </div>
          </div>
        </GridItem>

        <GridItem xs={12} sm={2} md={1}>
          <GridContainer justify="center">
            <GridItem>
              {focused === 'left' && (
                <Button
                  id="addPermission"
                  color="success"
                  className={classes.actionBtn}
                  onClick={this.handleAddClick}
                >
                  <ArrowRight className={classes.actionBtnIcon} />
                  <span>
                    {t('permissions.select.add')}
                    {newSelectedItems.length > 1 && 's'}
                  </span>
                </Button>
              )}
              {focused === 'right' && (
                <Button
                  id="removePermission"
                  color="danger"
                  className={classes.actionBtn}
                  onClick={this.handleAddClick}
                >
                  <ArrowLeft className={classes.actionBtnIcon} />
                  <span>
                    {t('permissions.select.remove')}
                    {newSelectedItems.length > 1 && 's'}
                  </span>
                </Button>
              )}
              {focused === '' && (
                <Button
                  disabled
                  className={classes.actionBtn}
                  onClick={this.handleAddClick}
                >
                  <Swap className={classes.actionBtnIcon} />
                  <span>Selecciona permisos</span>
                </Button>
              )}
            </GridItem>
          </GridContainer>
        </GridItem>

        <GridItem xs={12} sm={5}>
          <div className={classes.groupContainer}>
            <div className={classes.permListContainer}>
              {showHeader && (
                <List
                  className={`${classes.permList}
                   ${classes.addedGroupsContainer}`}
                >
                  <div className={classes.groupHeader}>
                    <h6>{t(`${translatePrefix}.select.title`)}</h6>
                  </div>

                  {selectedItems.map(data => (
                    <ListItem
                      key={data.id}
                      className={`${classes.permItem}
                  ${newSelectedItems.includes(data) && 'selected'}`}
                    >
                      <Button
                        onClick={() => this.handleItemSelected(data, 'right')}
                        simple
                        color="primary"
                        className={classes.permItemBtn}
                      >
                        {itemRender(data)}
                      </Button>
                    </ListItem>
                  ))}
                </List>
              )}
              {this.renderExtraData()}
              {this.renderNoPermissions()}
            </div>
          </div>
        </GridItem>
      </GridContainer>
    );
  }
}

/**
 * PermissionSelect PropTypes
 * @param classes - prop styles received from withStyles
 * @param items - prop input item list
 * @param selectedItems - prop added permissions list
 * @param loading - prop to know if loading data or filtering
 * @param t - prop from HOC with string translations
 * @param translatePrefix - prefix for string translations
 * @param extraData - prop to put fix data in right
 * @callback itemRender - callback function to render item
 * @callback updateItem - callback function called when the add
 * button is clicked
 * @callback searchItem - callback function called when search
 * input changes
 */

PermissionGroupSelect.propTypes = {
  classes: PropTypes.instanceOf(Object).isRequired,
  items: PropTypes.shape({}).isRequired,
  selectedItems: PropTypes.instanceOf(Array).isRequired,
  loading: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
  translatePrefix: PropTypes.string.isRequired,
  extraData: PropTypes.instanceOf(Object),
  itemRender: PropTypes.func.isRequired,
  updateItem: PropTypes.func.isRequired,
  searchItem: PropTypes.func.isRequired,
};

PermissionGroupSelect.defaultProps = {
  extraData: null,
};

export default withTranslation()(
  withStyles(PermissionGroupStyles)(PermissionGroupSelect),
);
